import React, { useState } from "react";
import Axios from "axios";
import LoadingSpin from "../utils/Loading";
import DashboardMui from "../dashboard/DashboardMui";
import { useHistory } from "react-router-dom";
import swal from "sweetalert";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import CancelIcon from "@material-ui/icons/Cancel";
import Grid from "@material-ui/core/Grid";
import PacienteSVG from "./../../images/paciente.svg";
import Container from "@material-ui/core/Container";
import CssBaseline from "@material-ui/core/CssBaseline";
import Typography from "@material-ui/core/Typography";
import useUserState from "../hooks/useUserState";
import useBDocTranslator from "../hooks/useBDocTranslator";
import {
  extractApiError,
  getMissingFieldKeys,
  isDuplicateEmailError,
} from "../../services/apiErrorMessage";

const useStyles = makeStyles((theme) => ({
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(2),
  },
  paper: {
    marginTop: theme.spacing(8),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "30px",
    backgroundColor: "white",
    boxShadow: "5px 5px 16px 1px #1b3342",
    borderRadius: "10px",
  },
}));

function PatientsRegister() {
  const [email, setEmail] = useState("");
  const [phone1, setPhone1] = useState("");
  const [firstName, setfirstName] = useState("");
  const [lastname, setLastname] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [gender, setGender] = useState("");
  const [country, setCountry] = useState("");
  const [loading, setLoading] = useState(false);
  // Client side validation feedback (which fields are missing)
  const [validationError, setValidationError] = useState("");
  // Real reason returned by the API, kept separate so it is never masked
  const [apiError, setApiError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  // Patient already registered with the typed email, if any
  const [existingPatient, setExistingPatient] = useState(null);
  let history = useHistory();
  const classes = useStyles();
  const userState = useUserState();
  const { t } = useBDocTranslator();

  const requiredFields = [
    { key: "email", label: t("Email"), value: email },
    { key: "country", label: t("Country"), value: country },
    { key: "phone1", label: t("Phone Number"), value: phone1 },
    { key: "firstName", label: t("First Name"), value: firstName },
    { key: "lastname", label: t("Last Name"), value: lastname },
    { key: "dateOfBirth", label: t("Date Of Birth"), value: dateOfBirth },
    { key: "gender", label: t("Gender"), value: gender },
  ];

  const missingKeys = getMissingFieldKeys(requiredFields);
  const missingLabels = requiredFields
    .filter((field) => missingKeys.includes(field.key))
    .map((field) => field.label);
  const isMissing = (key) => missingKeys.includes(key);

  // Resets the form fields only. The messages are handled separately so a
  // success message is not wiped right after being set.
  const clearForm = () => {
    document.getElementById("patient-form").reset();
    setDateOfBirth("");
    setEmail("");
    setGender("");
    setLastname("");
    setfirstName("");
    setPhone1("");
    setCountry("");
    setExistingPatient(null);
  };

  /**
   * Advisory pre-check: warns the provider when the email is already taken
   * before submitting, so the 409 can be avoided or handled with a shortcut.
   */
  const checkExistingPatient = async (value) => {
    const emailToCheck = (value || "").trim();
    setExistingPatient(null);
    if (!emailToCheck || !emailToCheck.includes("@")) return;

    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_API_URL_BASE}/patient/findByEmail/${encodeURIComponent(
          emailToCheck
        )}`
      );
      if (response.status === 200 && response.data && response.data.id) {
        setExistingPatient(response.data);
      }
    } catch (error) {
      // The pre-check never blocks the form, it only informs.
      console.log(error);
    }
  };

  const handleClick = (e) => {
    e.preventDefault();

    // Clear any previous feedback so a stale message is never shown again.
    setValidationError("");
    setApiError("");
    setSuccessMessage("");

    if (missingLabels.length > 0) {
      setValidationError(
        `${t("Missing required fields")}: ${missingLabels.join(", ")}`
      );
      return;
    }

    setLoading(true);
    Axios.post(
      `${process.env.REACT_APP_API_URL_BASE}/userPatient/createUserPatient`,
      {
        email,
        phone1,
        firstName,
        lastname,
        dateOfBirth,
        gender,
        password: "123456",
        country,
        rol_id: "2", // For Patients
        status_user_id: "1",
        created_by_userId: userState[0].user.id,
      },
      {
        headers: { Authorization: "Bearer " + userState[0].jwt }, //the jwt is a variable which holds the token
      }
    )
      .then((response) => {
        setLoading(false);
        if (!response.data || !response.data.jwt) {
          setApiError(t("Could not register the patient"));
          return;
        }
        swal(
          "success",
          t("Patient Registered, an Email was sending to Patient"),
          "success"
        );
        setSuccessMessage(
          t("Success, Patient Registered, an Email was sending to Patient")
        );

        clearForm();
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
        // Surface the actual reason instead of a generic "complete the fields"
        setApiError(extractApiError(error, t("Could not register the patient")));

        if (isDuplicateEmailError(error)) {
          checkExistingPatient(email);
        }
      });
  };

  const handleCancel = () => {
    history.goBack();
  };

  return (
    <DashboardMui
      sectionTitle={t("Patients")}
      description={t("Register new patient for your Practice")}
      icon={PacienteSVG}
    >
      <Container component="main">
        <CssBaseline />
        <div className={classes.paper}>
          <Typography component="h6" variant="h6" className="mt-2">
            {validationError && (
              <p className="text-danger">{validationError}</p>
            )}
            {apiError && <p className="text-danger">{apiError}</p>}
            {successMessage && (
              <p className="text-success">{successMessage}</p>
            )}
          </Typography>
          {existingPatient && (
            <div className="alert alert-warning w-100">
              <p className="mb-2">
                {t("A patient with this email is already registered")}
                {existingPatient.firstName
                  ? ` (${existingPatient.firstName} ${
                      existingPatient.lastname || ""
                    })`
                  : ""}
              </p>
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  history.push(`/admin/patient/edit/${existingPatient.id}`)
                }
              >
                {t("Edit existing patient")}
              </Button>
            </div>
          )}
          <form
            className="row m-3 shadow-sm p-3 mb-5 bg-white rounded justify-content-center"
            id="patient-form"
          >
            <div className="form-group col-md-12">
              <TextField
                required
                margin="normal"
                label={t("Email")}
                variant="outlined"
                id="email"
                value={email || ""}
                autoComplete="email"
                fullWidth
                placeholder="name@example.com"
                error={isMissing("email")}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setExistingPatient(null);
                }}
                onBlur={(e) => checkExistingPatient(e.target.value)}
              />
            </div>
            <div className="form-group col-md-6">
              <FormControl
                variant="outlined"
                className={classes.form}
                error={isMissing("country")}
              >
                <InputLabel required id="country">
                  {t("Country")}
                </InputLabel>
                <Select
                  labelId="country"
                  id="country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  label={t("Country")}
                >
                  <MenuItem value="CRC">Costa Rica</MenuItem>
                  <MenuItem value="USA">USA</MenuItem>
                  <MenuItem value="JMC">Jamaica</MenuItem>
                  <MenuItem value="NIC">Nicaragua</MenuItem>
                  <MenuItem value="BAH">Bahamas</MenuItem>
                </Select>
              </FormControl>
            </div>
            <div className="form-group col-md-6">
              <TextField
                required
                fullWidth
                margin="normal"
                label={t("Phone Number")}
                variant="outlined"
                id="phoneNumber"
                value={phone1 || ""}
                placeholder="888823456"
                error={isMissing("phone1")}
                onChange={(e) => setPhone1(e.target.value)}
              />
            </div>
            <div className="form-group col-md-6">
              <TextField
                margin="normal"
                label={t("First Name")}
                variant="outlined"
                id="firstName"
                fullWidth
                onChange={(e) => setfirstName(e.target.value)}
                required
                error={isMissing("firstName")}
                value={firstName || ""}
              />
            </div>
            <div className="form-group col-md-6">
              <TextField
                required
                fullWidth
                margin="normal"
                label={t("Last Name")}
                variant="outlined"
                id="lastname"
                onChange={(e) => setLastname(e.target.value)}
                error={isMissing("lastname")}
                value={lastname || ""}
              />
            </div>

            <div className="form-group col-md-6">
              <TextField
                variant="outlined"
                required
                fullWidth
                margin="normal"
                id="dateOfBirth"
                name="dateOfBirth"
                InputLabelProps={{
                  shrink: true,
                }}
                type="date"
                label={t("Date Of Birth")}
                error={isMissing("dateOfBirth")}
                onChange={(e) => setDateOfBirth(e.target.value)}
                value={dateOfBirth || ""}
              />
            </div>
            <div className="form-group col-md-6">
              <FormControl
                variant="outlined"
                className={classes.form}
                error={isMissing("gender")}
              >
                <InputLabel required id="gender">
                  {t("Gender")}
                </InputLabel>
                <Select
                  labelId="gender"
                  id="gender"
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  label={t("Gender")}
                >
                  <MenuItem value={""}>{t("None")}</MenuItem>
                  <MenuItem value={"MALE"}>{t("Male")}</MenuItem>
                  <MenuItem value={"FEMALE"}>{t("Female")}</MenuItem>
                  <MenuItem value={"OTHER"}>{t("Other")}</MenuItem>
                </Select>
              </FormControl>
            </div>
            <Grid className="m-3" container justify="flex-start">
              <Grid item>Default password: 123456</Grid>
            </Grid>
          </form>
        </div>
        <div className="row">
          <div className="col-md-12 d-flex justify-content-center m-3">
            <div className="p-3">
              <Button
                variant="contained"
                color="primary"
                startIcon={<CheckCircleOutlineIcon />}
                onClick={handleClick}
                disabled={loading}
              >
                {t("Save")}
              </Button>
            </div>

            <div className="p-3">
              <Button
                variant="contained"
                color="secondary"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
              >
                {t("Cancel")}
              </Button>
            </div>
          </div>
          <div className="col-md-12">
            {loading && (
              <div className="d-flex justify-content-center mt-3">
                <LoadingSpin type="spokes" color="#1d3557" />
              </div>
            )}
          </div>
        </div>
      </Container>
    </DashboardMui>
  );
}

export default PatientsRegister;
